import { FetchSingleResult } from '@lune/auth/api/fetch'
import { isMobile } from '@lune/common/device'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Snackbar,
  Typography
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { api as apiAlias } from '../../api/alias'
import { api as apiGroup } from '../../api/group'
import {
  ALIAS_DISABLED_SUFFIX,
  isAliasDisabled,
  type Alias,
  type AliasConflict,
  type Group as ApiGroup
} from '../../api/types'
import { Remove } from '../remove'
import { Group as GroupDesktop } from './desktop'
import { Group as GroupMobile } from './mobile'

const EXTRA_TIME_FOR_REFRESH_MS = 2 * 1000
const MAX_WATCH_ROUNDS = 30
const today = new Date().toISOString().split('T')[0]

interface GroupProps {
  id: string
  search: string
  me: string
  clockTick: number
  filterByMembers?: string[]
  onHasExpiredItems?: (
    groupId: string,
    groupName: string,
    hasExpired: boolean
  ) => void
  onHasConflicts?: (
    groupId: string,
    groupName: string,
    hasConflicts: boolean
  ) => void
  fetchUseSingle?: (
    clockTick: number,
    id: string
  ) => FetchSingleResult<ApiGroup>
}

export const Group: React.FC<GroupProps> = ({
  id,
  me: _me,
  search,
  clockTick,
  filterByMembers,
  onHasExpiredItems,
  onHasConflicts,
  fetchUseSingle = apiGroup.fetch.useSingle
}) => {
  const [localTick, setLocalTick] = useState(clockTick)
  const { ready, item, error, errorMessage } = fetchUseSingle(localTick, id)
  const [watchFor, setWatchFor] = useState({ effect: 'none', target: '' })
  const [watchFrom, setWatchFrom] = useState<string | undefined>()
  const [watchRounds, setWatchRounds] = useState(0)
  const [showTimeoutDialog, setShowTimeoutDialog] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const [deletingAlias, setDeletingAlias] = useState<string>()
  const [deletingAliasInProgress, setDeletingAliasInProgress] =
    useState<string>()
  const [deletingGroup, setDeletingGroup] = useState(false)
  const [opResult, setOpResult] = useState<{
    status: 'success' | 'error'
    message: string
  }>()
  const [aliasDetails, setAliasDetails] = useState<Alias[]>([])

  const lastItemRef = useRef<ApiGroup | undefined>(undefined)

  // Sync with external clock tick (global refresh button, etc.)
  useEffect(() => {
    setLocalTick(Date.now())
  }, [clockTick])

  // Fetch alias metadata (description, expires) whenever the group refreshes
  useEffect(() => {
    void apiAlias
      .all(id)
      .then(setAliasDetails)
      .catch(() => {
        setAliasDetails([])
      })
  }, [localTick, id])

  // Notify parent when expired alias presence changes
  useEffect(() => {
    if (onHasExpiredItems === undefined) return
    const hasExpired = aliasDetails.some(
      (a) => a.expires !== '' && a.expires < today
    )
    onHasExpiredItems(id, lastItemRef.current?.name ?? id, hasExpired)
    return () => {
      onHasExpiredItems(id, '', false)
    }
  }, [aliasDetails, id, onHasExpiredItems])

  // Notify parent when alias conflict presence changes
  useEffect(() => {
    if (onHasConflicts === undefined || item === undefined) return
    const aliasSet = new Set(item.aliases)
    const hasConflicts = item.aliases.some((a) => {
      if (isAliasDisabled(a)) return false
      const [localPart, domain] = a.split('@')
      return aliasSet.has(`${localPart}${ALIAS_DISABLED_SUFFIX}@${domain}`)
    })
    onHasConflicts(id, item.name, hasConflicts)
    return () => {
      onHasConflicts(id, '', false)
    }
  }, [item, id, onHasConflicts])

  // On mount: start watching only if this group is the reload target or the move source
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const reloadId = params.get('reload')
    const fromId = params.get('from')
    const moved = params.get('moved')
    const added = params.get('added')
    const removed = params.get('removed')

    if (reloadId === id) {
      setWatchFor({
        effect:
          added !== null
            ? 'added'
            : moved !== null
              ? 'moved'
              : removed !== null
                ? 'removed'
                : 'none',
        target: added ?? moved ?? removed ?? ''
      })
      setWatchFrom(fromId ?? undefined)
    } else if (fromId === id && moved !== null) {
      setWatchFor({
        effect: 'moved',
        target: moved
      })
      setWatchFrom(fromId)
    }
  }, [id])

  // Polling: after each fetch completes, check if the expected change has landed
  useEffect(() => {
    if (watchFor.effect === 'none' || !ready) return

    let isDone = false

    if (watchFor.effect === 'removed' && watchFor.target === id) {
      // Watching for group removal
      isDone = item === undefined && error === 'not-found'
    } else if (item !== undefined) {
      // Watching for alias change
      const aliasInList = item.aliases.includes(watchFor.target)
      if (watchFor.effect === 'moved') {
        if (watchFrom === id) {
          // I am source: isDone when alias leaves
          isDone = !aliasInList
        } else {
          // I am target: isDone when alias arrives
          isDone = aliasInList
        }
      } else {
        isDone = watchFor.effect === 'removed' ? !aliasInList : aliasInList
      }
    }

    if (isDone || watchRounds >= MAX_WATCH_ROUNDS) {
      if (isDone) {
        // Only report success if we are NOT the source group of a move
        // or if we were watching for removal and it worked.
        if (watchFor.effect === 'moved' && watchFrom === id) {
          // Alias left the source. Quietly stop watching.
        } else {
          setOpResult({
            status: 'success',
            message:
              watchFor.effect === 'moved'
                ? 'Operation successful'
                : watchFor.effect === 'toggled'
                  ? isAliasDisabled(watchFor.target)
                    ? 'Alias disabled'
                    : 'Alias enabled'
                  : `Alias '${watchFor.target}' ${watchFor.effect} OK`
          })
        }
      } else {
        // Timeout reached. Check if it's at source or gone.
        if (
          watchFor.effect === 'moved' &&
          watchFrom === id &&
          item !== undefined
        ) {
          const aliasInList = item.aliases.includes(watchFor.target)
          if (aliasInList) {
            setOpResult({
              status: 'error',
              message: 'Operation failed, item back at origin'
            })
          }
        } else if (watchFor.effect === 'moved' && watchFrom !== id) {
          // I am target and it never arrived.
          setShowTimeoutDialog(true)
        } else if (watchFor.effect !== 'moved') {
          // Other effects (added, removed) timeout
          setShowTimeoutDialog(true)
        }
      }

      setWatchFor({ effect: 'none', target: '' })
      setWatchFrom(undefined)
      setWatchRounds(0)
      return
    }

    const timer = setTimeout(() => {
      setWatchRounds((r) => r + 1)
      setLocalTick(Date.now())
    }, EXTRA_TIME_FOR_REFRESH_MS)

    return () => clearTimeout(timer)
  }, [watchFor, item, ready, watchRounds, id, error, watchFrom])

  // Keep the last successfully fetched item so the card never blinks during polling re-fetches
  if (item !== undefined) lastItemRef.current = item
  const displayItem = item ?? lastItemRef.current

  // Conditional returns MUST come after all hooks (useState, useEffect, useRef)
  if (deleted) return null

  if ((ready && item === undefined) || errorMessage !== undefined) {
    const deletionConfirmed =
      watchFor.effect === 'removed' && watchFor.target === id
    if (
      (error === 'not-found' ||
        errorMessage?.toLowerCase().includes('not found') ||
        errorMessage?.toLowerCase().includes('fetching resource') ||
        deletionConfirmed) &&
      !deleted
    ) {
      // We were deleted. Hide!
      console.debug('Group deleted. Hide!')
      setDeleted(true)
      return null
    }
    return (
      <Alert severity="error">
        Error loading group: {errorMessage ?? 'No error message'}
      </Alert>
    )
  }

  // First load — no data cached yet, show skeleton
  if (displayItem === undefined) {
    return (
      <Box
        sx={{
          alignSelf: 'start',
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50'
        }}
      >
        <CircularProgress size={16} />
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          {id}
        </Typography>
      </Box>
    )
  }

  // Member-based filtering
  if (filterByMembers !== undefined) {
    const groupMembers = displayItem.members ?? []

    let shouldDisplay: boolean
    if (filterByMembers.length === 0) {
      // "Nobody" filter: show groups with zero members
      shouldDisplay = groupMembers.length === 0
    } else {
      // Standard filter: all targets must be present in group members
      shouldDisplay = filterByMembers.every((targetMember) =>
        groupMembers.includes(targetMember)
      )
    }

    if (!shouldDisplay) {
      return null
    }
  }

  const safeSearch = search ?? ''
  const searchLocal = safeSearch.includes('@')
    ? safeSearch.split('@')[0]
    : safeSearch

  // Merge Google alias list with local metadata
  const enrichedAliases: Alias[] = displayItem.aliases.map((email) => {
    const detail = aliasDetails.find((d) => d.alias === email)
    return detail ?? { group: id, alias: email, description: '', expires: '' }
  })

  const aliases: Alias[] =
    searchLocal === ''
      ? enrichedAliases
      : enrichedAliases.filter((a) =>
          a.alias
            .split('@')[0]
            .toLowerCase()
            .includes(searchLocal.toLowerCase())
        )

  // Show a pending chip for an alias we're waiting to arrive (added/moved/toggled)
  const pendingAlias =
    (watchFor.effect === 'added' ||
      watchFor.effect === 'moved' ||
      watchFor.effect === 'toggled') &&
    !displayItem.aliases.includes(watchFor.target)
      ? watchFor.target
      : undefined

  const onDeleteAlias = (alias: string): void => {
    setDeletingAlias(alias)
  }

  const onDeleteGroup = (): void => {
    setDeletingGroup(true)
  }

  const onAliasUpdated = (updated: Alias): void => {
    setAliasDetails((prev) =>
      prev.some((d) => d.alias === updated.alias)
        ? prev.map((d) => (d.alias === updated.alias ? updated : d))
        : [...prev, updated]
    )
  }

  const resolveConflict = async (
    alias: string,
    enable: boolean
  ): Promise<void> => {
    const [localPart, domain] = alias.split('@')
    const disabledForm = `${localPart}${ALIAS_DISABLED_SUFFIX}@${domain}`
    const disappearing = enable ? disabledForm : alias
    setDeletingAliasInProgress(disappearing)
    const method = enable ? 'enable' : 'disable'
    await apiAlias
      .item({ group: id, alias })
      .methods[method].post({})
      .then(() => {
        setOpResult({
          status: 'success',
          message: enable ? 'Alias enabled' : 'Alias disabled'
        })
        setWatchFor({ effect: 'removed', target: disappearing })
      })
      .catch((err: Error) => {
        setOpResult({ status: 'error', message: err.message })
      })
      .finally(() => {
        setDeletingAliasInProgress(undefined)
      })
  }

  const removeAlias = async (alias: string): Promise<void> => {
    setDeletingAliasInProgress(alias)
    await apiAlias
      .remove({ group: id, alias })
      .then(() => {
        setOpResult({
          status: 'success',
          message: `Alias '${alias}' deleted`
        })
        setWatchFor({
          effect: 'removed',
          target: alias
        })
      })
      .catch((err: Error) => {
        setOpResult({
          status: 'error',
          message: err.message
        })
      })
      .finally(() => {
        setDeletingAliasInProgress(undefined)
      })
  }

  const toggleAlias = async (alias: Alias): Promise<void> => {
    const [localPart, domain] = alias.alias.split('@')
    const disabled = isAliasDisabled(alias.alias)
    const baseLocalPart = disabled
      ? localPart.slice(0, -ALIAS_DISABLED_SUFFIX.length)
      : localPart
    const baseAlias = `${baseLocalPart}@${domain}`
    const newAlias = disabled
      ? baseAlias
      : `${localPart}${ALIAS_DISABLED_SUFFIX}@${domain}`

    const method = disabled ? 'enable' : 'disable'
    await apiAlias
      .item({ group: id, alias: baseAlias })
      .methods[method].post({})
      .then(() => {
        setWatchFor({ effect: 'toggled', target: newAlias })
        setWatchFrom(alias.alias)
      })
      .catch((err: Error) => {
        setOpResult({ status: 'error', message: err.message })
      })
  }

  const removeGroup = async (): Promise<void> => {
    await apiGroup
      .remove(id)
      .then(() => {
        setOpResult({
          status: 'success',
          message: `Group '${id}' deleted`
        })
        setWatchFor({
          effect: 'removed',
          target: id
        })
      })
      .catch((err: Error) => {
        setOpResult({
          status: 'error',
          message: err.message
        })
      })
      .finally(() => {
        setDeletingGroup(false)
      })
  }

  const View = isMobile() ? GroupMobile : GroupDesktop

  // Detect aliases where both the enabled and disabled form coexist (crashed toggle)
  const aliasSet = new Set(displayItem.aliases)
  const conflicts: AliasConflict[] = displayItem.aliases
    .filter((a) => !isAliasDisabled(a))
    .flatMap((enabled) => {
      const [localPart, domain] = enabled.split('@')
      const disabled = `${localPart}${ALIAS_DISABLED_SUFFIX}@${domain}`
      return aliasSet.has(disabled) ? [{ enabled, disabled }] : []
    })

  return (
    <div id={id}>
      <View
        name={displayItem.name}
        email={displayItem.email}
        members={displayItem.members}
        aliases={aliases}
        allAliases={enrichedAliases}
        search={searchLocal}
        pendingAlias={pendingAlias}
        deletingAlias={
          deletingAliasInProgress ??
          (watchFor.effect === 'toggled' && watchFrom !== undefined
            ? watchFrom
            : (watchFor.effect === 'removed' ||
                  (watchFor.effect === 'moved' && watchFrom === id)) &&
                watchFor.target !== id
              ? watchFor.target
              : undefined)
        }
        onDeleteAlias={onDeleteAlias}
        onDeleteGroup={onDeleteGroup}
        onAliasUpdated={onAliasUpdated}
        onToggleAlias={(alias) => {
          void toggleAlias(alias)
        }}
        conflicts={conflicts}
        onResolveConflict={(alias, enable) => {
          void resolveConflict(alias, enable)
        }}
        isGroupDeleting={
          watchFor.effect === 'removed' && watchFor.target === id
        }
      />
      {deletingAlias !== undefined ? (
        <Remove
          open={deletingAlias !== undefined}
          kind="alias"
          id={deletingAlias}
          name={deletingAlias}
          onDelete={removeAlias}
          onCancel={() => {}}
          onClose={() => setDeletingAlias(undefined)}
        />
      ) : null}
      {deletingGroup ? (
        <Remove
          open={deletingGroup}
          kind="group"
          id={id}
          name={id}
          onDelete={removeGroup}
          onCancel={() => {}}
          onClose={() => setDeletingGroup(false)}
        />
      ) : null}
      <Snackbar
        open={opResult !== undefined}
        autoHideDuration={3000}
        onClose={() => {
          setOpResult(undefined)
        }}
      >
        <Alert severity={opResult?.status ?? 'info'}>{opResult?.message}</Alert>
      </Snackbar>
      <Dialog
        open={showTimeoutDialog}
        onClose={() => setShowTimeoutDialog(false)}
      >
        <DialogContent>
          <DialogContentText>
            Operation failed. Alias &apos;{watchFor.target}&apos; is gone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTimeoutDialog(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
